import {
  forwardRef,
  useEffect,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../lib/ui.js';

/* --------------------------------- Botão -------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-nova-500 to-pulse-500 text-night-950 font-semibold shadow-lg shadow-nova-600/30 hover:brightness-110 active:brightness-95',
  secondary:
    'bg-night-800 text-night-200 border border-night-600 hover:bg-night-700 active:bg-night-800',
  ghost: 'bg-transparent text-night-300 hover:text-night-200 hover:bg-night-800/60',
  danger: 'bg-life/15 text-life border border-life/40 hover:bg-life/25',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', fullWidth, loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 py-3 text-base transition',
        'disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

/* --------------------------------- Painel ------------------------------- */

export function Panel({
  children,
  className,
  as: Tag = 'section',
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'article';
}): ReactNode {
  return <Tag className={cn('glass rounded-3xl p-5', className)}>{children}</Tag>;
}

/* --------------------------------- Campo -------------------------------- */

export type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-sm font-medium text-night-300">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-describedby={hint || error ? hintId : undefined}
        aria-invalid={error ? true : undefined}
        className={cn(
          'min-h-12 rounded-2xl border bg-night-900/80 px-4 py-3 text-lg text-night-200 placeholder:text-night-400/70',
          'transition outline-none focus:border-pulse-500',
          error ? 'border-life/70' : 'border-night-600',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={hintId} className="text-sm text-life">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-night-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

/* -------------------------------- Alternador ----------------------------- */

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  description,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  description?: string;
}): ReactNode {
  return (
    <label
      className={cn(
        'flex min-h-12 cursor-pointer items-center justify-between gap-4 rounded-2xl bg-night-900/60 px-4 py-3',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span className="flex flex-col">
        <span className="text-base text-night-200">{label}</span>
        {description ? <span className="text-xs text-night-400">{description}</span> : null}
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          'relative h-7 w-12 shrink-0 rounded-full transition',
          checked ? 'bg-pulse-500' : 'bg-night-600',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-pulse-400',
        )}
      >
        <span
          className={cn(
            'absolute top-1 size-5 rounded-full bg-night-950 transition-all',
            checked ? 'left-6' : 'left-1',
          )}
        />
      </span>
    </label>
  );
}

/* --------------------------------- Stepper ------------------------------- */

export function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 rounded-2xl bg-night-900/60 px-4 py-2">
      <span id={id} className="text-base text-night-200">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          className="size-11 min-h-11 rounded-xl p-0 text-xl"
          aria-label={`Diminuir ${label}`}
          disabled={disabled || value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
        >
          −
        </Button>
        <output
          aria-labelledby={id}
          className="tabular w-10 text-center text-xl font-semibold text-night-200"
        >
          {value}
        </output>
        <Button
          variant="secondary"
          className="size-11 min-h-11 rounded-xl p-0 text-xl"
          aria-label={`Aumentar ${label}`}
          disabled={disabled || value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
        >
          +
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------- Overlay ------------------------------- */

export function Overlay({
  open,
  title,
  children,
  onClose,
  dismissible = false,
  tone = 'neutral',
  testId,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose?: () => void;
  dismissible?: boolean;
  tone?: 'neutral' | 'danger' | 'success' | 'star';
  testId?: string;
}): ReactNode {
  useEffect(() => {
    if (!open || !dismissible || !onClose) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, dismissible, onClose]);

  if (!open) return null;

  const toneRing =
    tone === 'danger'
      ? 'ring-life/40'
      : tone === 'success'
        ? 'ring-pulse-500/40'
        : tone === 'star'
          ? 'ring-star/40'
          : 'ring-night-600/60';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-night-950/80 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
    >
      <div
        className={cn(
          'animate-rise glass w-full max-w-md rounded-3xl p-6 ring-1',
          'max-h-[90dvh] overflow-y-auto',
          toneRing,
        )}
      >
        <h2 className="text-balance mb-3 text-2xl font-semibold text-night-200">{title}</h2>
        {children}
      </div>
    </div>
  );
}
